import React from 'react';
import {Paper, Tooltip, Typography} from '@mui/material';
import type {SxProps, Theme} from '@mui/material';

interface KpiCardProps {
    /** Small caption above the value. */
    label: React.ReactNode;
    /** The headline value (rendered in an h5). */
    value: React.ReactNode;
    /** Optional caption below the value. */
    subtext?: React.ReactNode;
    /** Color of the headline value (default theme text.primary). */
    valueColor?: string;
    /** Wraps the card in a help Tooltip when provided. */
    tooltip?: string;
    /** Makes the card a clickable button-like surface. */
    onClick?: () => void;
    backgroundColor?: string;
    borderColor?: string;
    minWidth?: number;
    /** Extra styling for the subtext caption (e.g. multi-line lineHeight). */
    subtextSx?: SxProps<Theme>;
}

/** Stat card shell shared by the IRP workbook dashboards (Barrier Effectiveness,
 *  Report Clustering): caption label, bold value, optional caption subtext. Intended
 *  for the plain stat cards — cards with chips or branchy clickable subtext stay inline. */
export const KpiCard: React.FC<KpiCardProps> = ({
    label,
    value,
    subtext,
    valueColor = 'text.primary',
    tooltip,
    onClick,
    backgroundColor,
    borderColor = 'divider',
    minWidth = 140,
    subtextSx,
}) => {
    const card = (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                px: 2, py: 1.5, minWidth, textAlign: 'center',
                border: '1px solid', borderColor,
                ...(backgroundColor ? {backgroundColor} : {}),
                ...(onClick
                    ? {cursor: 'pointer', '&:hover': {borderColor: 'primary.main'}}
                    : tooltip ? {cursor: 'help'} : {}),
            }}
        >
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5" sx={{fontWeight: 'bold', color: valueColor}}>{value}</Typography>
            {subtext != null && (
                <Typography variant="caption" color="text.secondary" sx={{display: 'block', ...subtextSx}}>
                    {subtext}
                </Typography>
            )}
        </Paper>
    );
    return tooltip ? <Tooltip title={tooltip} arrow>{card}</Tooltip> : card;
};
